import React from "react";

import clsx from "clsx";

import IconButton from "@busbud/design-system-components/dist/IconButton";
import CrossSM from "@busbud/design-system-components/dist/Icons/Cross/SM";
import Numerical from "@busbud/design-system-components/dist/Numerical";
import { Label } from "@busbud/horizon";

import { Markdown } from "@app/components/markdown";
import { useAppContext } from "@app/helpers/hooks";
import type { CurrencyCode } from "@app/types/currency";

import { useStyles } from "./credits-redeemed-layout.styles";
import { LocalizedPrice } from "../../../../../../components/localized-price";

interface Props {
  balance: number;
  currency: CurrencyCode;
  onUnredeem?: () => void;
  label: string;
  has_error?: boolean;
  error_text?: string;
  isWalmartDiscountCode?: boolean;
}

export const CreditsRedeemedLayout: React.FC<Props> = ({
  balance,
  currency,
  label,
  onUnredeem,
  has_error = false,
  isWalmartDiscountCode = false,
  error_text
}) => {
  const { classes } = useStyles();
  const { translator } = useAppContext();

  if (balance === 0 && !isWalmartDiscountCode) {
    return null;
  }

  return (
    <>
      <div
        className={clsx([
          isWalmartDiscountCode ? "items-start" : "items-center",
          "my-050 flex justify-between rounded-sm bg-color-scheme-literal-green-100 p-100"
        ])}
      >
        {!!onUnredeem && (
          <IconButton
            size="xs"
            variant="contained"
            onClick={onUnredeem}
            color="tertiary"
            data-testid="remove-code"
            classes={{
              containedTertiary: classes.resetButton
            }}
          >
            <CrossSM />
          </IconButton>
        )}
        <div className="flex flex-grow flex-col justify-start pl-100">
          <Label
            as="div"
            size="md"
            className="text-color-scheme-literal-green-600"
            fontWeight="bold"
            data-testid="coupon-code"
          >
            {label}
          </Label>
          {!!isWalmartDiscountCode && (
            <Label
              as="p"
              size="md"
              fontWeight="bold"
              data-testid="walmart-coupon-code"
              className="mt-050 block max-w-[270px]"
            >
              <Markdown
                Component="span"
                source={translator.t("!checkout.summary.discount-code-walmart")}
              ></Markdown>
            </Label>
          )}
        </div>
        {!isWalmartDiscountCode && (
          <Numerical
            size="md"
            className="text-color-scheme-literal-green-800"
            fontWeight="bold"
          >
            <span className="whitespace-nowrap">
              <LocalizedPrice
                price_in_cents={-1 * balance}
                currency={currency}
              />
            </span>
          </Numerical>
        )}
      </div>

      {!!has_error && (
        <Label
          as="p"
          size="sm"
          className="text-color-scheme-literal-red-500"
          fontWeight="bold"
        >
          {error_text}
        </Label>
      )}
    </>
  );
};
